/**
 * 코드 → 사람이 읽는 이름.
 *
 * `/metrics/ranking` 의 `entity_id` 는 **코드**다 (`busi='PI'`, `zcode='11'`).
 * 순위표에 `PI` 라고 적으면 아무도 읽지 못한다. 이름은 `/meta/filters` 가
 * `config/codes/*.csv` 에서 붙여 내려주므로 프론트에 하드코딩하지 않는다.
 */

import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { fetchPlain } from './api'
import type { RankEntity } from './types'

interface FilterOptions {
  options: Record<string, { code: string; label: string; n: number | null }[]>
  data_version: string
}

/** ranking 의 entity 값 → `/meta/filters` 의 dim 이름 */
const DIM_OF: Partial<Record<RankEntity, string>> = {
  busi: 'busi',
  zcode: 'zcode',
  maker: 'maker',
  chger_type: 'chger_type',
  kind: 'kind',
}

const FLOOR_LABEL: Record<string, string> = { F: '지상', B: '지하' }

export function useEntityLabel() {
  const { data } = useQuery({
    queryKey: ['meta', 'filters'],
    queryFn: ({ signal }) => fetchPlain<FilterOptions>('/meta/filters', signal),
    staleTime: 5 * 60 * 1000,
  })

  return useMemo(() => {
    const map = new Map<string, string>()
    for (const [dim, opts] of Object.entries(data?.options ?? {})) {
      for (const o of opts) map.set(`${dim}|${o.code}`, o.label)
    }
    return (entity: RankEntity, code: string): string => {
      if (entity === 'floor_type') return FLOOR_LABEL[code] ?? code
      if (entity === 'year') return `${code}년`
      const dim = DIM_OF[entity]
      return (dim && map.get(`${dim}|${code}`)) || code
    }
  }, [data])
}
